/* eslint react-hooks/rules-of-hooks: 0 */
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Alert,
  Backdrop,
  Box,
  Button,
  CircularProgress,
  Collapse,
  FormControl,
  IconButton,
  InputLabel,
  TextField,
  Typography,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { StyledEngineProvider } from '@mui/material/styles';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import moment from 'moment';
import ReactCountryFlag from 'react-country-flag';
import isStrongPassword from 'validator/lib/isStrongPassword';
import ReCAPTCHA from 'react-google-recaptcha';

import StyledSelect from './input/StyledSelect';
import StyledDateSelect from './input/StyledDateSelect';
import StyledTextField from './input/StyledTextField';

import {
  fetchCities,
  fetchCountries,
  fetchCountriesFromLibrary,
} from '../store/actions/location-actions';
import {
  clearKYCUserData,
  getUserKYCData,
  setCancelIncoming,
} from '../store/actions/kyc-actions';
import {
  authClearRecaptchaTokenValidated,
  authValidateRecaptchaToken,
} from '../store/actions/auth-actions';
import {
  authProcessorCheck,
  authProcessorClearError,
  authProcessorClearFireCheck,
  authProcessorClearState,
  authProcessorLogin,
  authProcessorSetError,
} from '../store/actions/authProcessor-actions';
import { setCreatedUser } from '../store/actions/user-actions';

import {
  CountryDialIsoFlags,
  DefactoCountryFlag,
  NationalityCountryCodes,
  SovereignCountriesList,
} from '../library/dssc-country-nationality-municipality-codes';
import {
  isEmpty,
  usePrevious,
  emailRegex,
  onlyLatinCharacters,
} from '../library/utils';
import config from '../config';

let pollingTimer = null;

const User = (props) => {
  const {
    model,
    hideMiddleName,
    hideState,
    hideZipCode,
    backdropStyles,
    onSubmit,
    submitButtonText,
    withPassword,
    withRecaptcha,
    touched: propTouched,
    isBankIdEnabled,
  } = props;
  const dispatch = useDispatch();
  let identityNumbertInputTimer = null;
  const pwReqConfigValues = config.REACT_APP_PASSWORD_REQUIREMENTS.split('|');

  // Selectors
  const recaptchaTokenValidated = useSelector(
    (state) => state.auth.recaptchaTokenValidated
  );
  const userKYCData = useSelector((state) => state.kyc.userKYCData);
  const isKYCLoading = useSelector((state) => state.kyc.loading);
  const errorKYC = useSelector((state) => state.kyc.error);
  const errorMessageKYC = useSelector((state) => state.kyc.errorMessage);
  const senderCountriesFetched = useSelector(
    (state) => state.location.senderCountriesFetched
  );
  const senderCountries = useSelector(
    (state) => state.location.senderCountries
  );
  const sendingCities = useSelector((state) => state.location.sendingCities);

  const authProcessorStarted = useSelector(
    (state) => state.authProcessor.authProcessorStarted
  );
  const authProcessorCancelled = useSelector(
    (state) => state.authProcessor.authProcessorCancelled
  );
  const authProcessorFireCheck = useSelector(
    (state) => state.authProcessor.authProcessorFireCheck
  );
  const authProcessorRedirectUrl = useSelector(
    (state) => state.authProcessor.authProcessorRedirectUrl
  );
  const authProcessorRequest = useSelector(
    (state) => state.authProcessor.authProcessorRequest
  );
  const authProcessorAuthCode = useSelector(
    (state) => state.authProcessor.authProcessorAuthCode
  );
  const isAuthProcessorLoading = useSelector(
    (state) => state.authProcessor.authProcessorLoading
  );
  const isAuthProcessorError = useSelector(
    (state) => state.authProcessor.authProcessorError
  );
  const authProcessorErrorMessage = useSelector(
    (state) => state.authProcessor.authProcessorErrorMessage
  );

  // States
  const [motherMaidenName, setMotherMaidenName] = useState('');
  const [touched, setTouched] = useState(!!propTouched);
  const [birthDateError, setBirthDateError] = useState('');
  const [sendingCountryOptions, setSendingCountryOptions] = useState([]);
  const [sendingCityOptions, setSendingCityOptions] = useState([]);
  const [firstname, setFirstName] = useState(model.firstname || '');
  const [middlename, setMiddleName] = useState(model.middlename || '');
  const [lastname, setLastName] = useState(model.lastname || '');
  const [gender, setGender] = useState(model.gender || '');
  const [address1, setAddress1] = useState(model.address1 || '');
  const [address2, setAddress2] = useState(model.address2 || '');
  const [addressCity, setAddressCity] = useState(
    model.city || model.addressCity || ''
  );
  const [addressState, setAddressState] = useState(
    model.state || model.addressState || ''
  );
  const [addressZipCode, setAddressZipCode] = useState(
    model.zipCode || model.addressZipCode || ''
  );
  const [addressCountry, setAddressCountry] = useState(
    model.country ||
      model.addressCountry ||
      // config.REACT_APP_DEFAULT_COUNTRY_ID ||
      config.REACT_APP_DEFAULT_COUNTRY_ISO2_CODE ||
      ''
  );
  const [email, setEmail] = useState(model.email || '');
  const [nationality, setNationality] = useState(
    model.nationality || config.REACT_APP_DEFAULT_COUNTRY_ISO2_CODE || ''
  );
  const [birthDate, setBirthDate] = useState(
    model.birthDate
      ? new Date(model.birthDate)
      : new Date(new Date().setFullYear(new Date().getFullYear() - 18))
  );
  const [identityNumber, setIdentityNumber] = useState('');
  const [phoneNumber, setPhoneNumber] = useState(
    model.phoneNumber
      ?.trim()
      .replace(
        new RegExp(
          '^\\+' +
            CountryDialIsoFlags.find(
              (c) => c.isoCode.toLowerCase() === model.country?.toLowerCase()
            )?.dialCode?.replace(/^\+/, '')
        ),
        ''
      )
      .replace(
        new RegExp(
          '^' +
            CountryDialIsoFlags.find(
              (c) => c.isoCode.toLowerCase() === model.country?.toLowerCase()
            )?.dialCode?.replace(/^\+/, '')
        ),
        ''
      )
      .replace(new RegExp('^\\d+\\-'), '') || ''
  );
  const [password, setPassword] = useState('');
  const [repeatPassword, setRepeatPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showRepeatPassword, setShowRepeatPassword] = useState(false);
  const [recaptchaToken, setRecaptchaToken] = useState(null);
  const prevUserKYCData = usePrevious(userKYCData);
  const [configCountrySSNFormat, setConfigCountrySSNFormat] = useState([]);
  const [idPlaceHolder, setIdPlaceHolder] = useState('Identity Number');
  const [ssnLength, setSSNLength] = useState(100);

  // Effects
  useEffect(() => {
    // dispatch(authProcessorClearState());
    return () => {
      clearTimeout(pollingTimer);
      dispatch(authProcessorClearState());
    };
  }, [dispatch]);

  useEffect(() => {
    if (
      authProcessorStarted &&
      authProcessorFireCheck &&
      !!!authProcessorRedirectUrl
    ) {
      dispatch(authProcessorClearFireCheck());
      pollingTimer = setTimeout(() => {
        dispatch(authProcessorCheck(authProcessorRequest));
      }, 1000);
    }
  }, [
    authProcessorFireCheck,
    authProcessorRedirectUrl,
    authProcessorRequest,
    authProcessorStarted,
    dispatch,
  ]);

  useEffect(() => {
    if (authProcessorStarted && authProcessorRedirectUrl) {
      window.location.href = authProcessorRedirectUrl;
    }
  }, [authProcessorStarted, authProcessorRedirectUrl, props, dispatch]);

  useEffect(() => {
    if (authProcessorAuthCode) {
      dispatch(setCreatedUser());
    }
  }, [authProcessorAuthCode, dispatch]);

  useEffect(() => {
    if (authProcessorCancelled) {
      dispatch(authProcessorSetError('Client cancelled sign-in.'));
    }
  }, [authProcessorCancelled, dispatch]);

  const clearAuthError = () => {
    dispatch(authProcessorClearError());
  };

  useEffect(() => {
    const tempConfig = config.REACT_APP_AUTH_PROCESSOR_COUNTRY_SSN_FORMAT.split(
      ','
    ).map((cf) => {
      const countryFormat = cf.split('|');
      return {
        countryCode: countryFormat[0],
        countryName: NationalityCountryCodes.find(
          (c) => c.alpha_2_code === countryFormat[0]
        )?.en_short_name,
        value: countryFormat[0],
        display: NationalityCountryCodes.find(
          (c) => c.alpha_2_code === countryFormat[0]
        )?.en_short_name,
        listItemIcon: (
          <ReactCountryFlag svg countryCode={countryFormat[0].toUpperCase()} />
        ),
        placeHolder: countryFormat[1],
        length: parseInt(countryFormat[2]),
      };
    });
    setConfigCountrySSNFormat(tempConfig);
    setIdPlaceHolder(
      tempConfig.find(
        (cf) =>
          cf.countryCode === config.REACT_APP_AUTH_PROCESSOR_DEFAULT_COUNTRY
      )?.placeHolder || 'Identity Number'
    );
    setSSNLength(
      tempConfig.find(
        (cf) =>
          cf.countryCode === config.REACT_APP_AUTH_PROCESSOR_DEFAULT_COUNTRY
      )?.length || 100
    );
  }, []);

  useEffect(() => {
    if (isEmpty(senderCountries) && !senderCountriesFetched) {
      if (config.REACT_APP_FETCH_SOURCE_COUNTRY_MODE === 'LIBRARY') {
        dispatch(fetchCountriesFromLibrary('SENDING'));
      } else {
        dispatch(fetchCountries(null, 'SENDER'));
      }
    } else {
      const countryOptions = [];

      senderCountries.forEach((c) => {
        const country = CountryDialIsoFlags.find(
          (cif) => cif.isoCode === c.isoCode
        );
        countryOptions.push({
          value: c.isoCode.toString(),
          display: country?.name,
          listItemIcon:
            c.isoCode.toUpperCase() === 'XX' ? (
              <img
                style={{
                  display: 'inline-block',
                  width: '1em',
                  height: '1em',
                  verticalAlign: 'middle',
                }}
                alt={c.isoCode.toUpperCase()}
                src={DefactoCountryFlag('xx')}
              />
            ) : (
              <ReactCountryFlag svg countryCode={c.isoCode.toUpperCase()} />
            ),
        });
      });

      setSendingCountryOptions(countryOptions);

      const setCountryCode =
        senderCountries.find(
          (sc) => sc.id.toString() === model.country?.toString()
        )?.isoCode ||
        senderCountries.find(
          (sc) => sc.id.toString() === model.addressCountry?.toString()
        )?.isoCode ||
        config.REACT_APP_DEFAULT_COUNTRY_ISO2_CODE ||
        countryOptions[0].value;
      setAddressCountry(setCountryCode);

      if (
        config.REACT_APP_USER_KYC_COUNTRIES &&
        config.REACT_APP_USER_KYC_COUNTRIES.split(',').includes(
          setCountryCode?.toUpperCase()
        ) &&
        !(model.id && true)
      ) {
        setIdentityNumber('');
        setIdPlaceHolder(
          configCountrySSNFormat.find((cf) => cf.countryCode === setCountryCode)
            ?.placeHolder || 'Identity Number'
        );
        setSSNLength(
          configCountrySSNFormat.find((cf) => cf.countryCode === setCountryCode)
            ?.length || 100
        );
      }
    }
  }, [senderCountries, senderCountriesFetched, model, dispatch, configCountrySSNFormat]);

  useEffect(() => {
    if (config.REACT_APP_USE_SENDING_CITIES === 'true') {
      if (
        isEmpty(
          sendingCities[
            senderCountries?.find((c) => c.isoCode === addressCountry)?.id
          ]
        )
      ) {
        dispatch(
          fetchCities(
            null,
            null,
            senderCountries?.find((c) => c.isoCode === addressCountry)?.id,
            config.REACT_APP_TENANT,
            'SENDING'
          )
        );
      } else {
        const cityOptions = [];

        sendingCities[
          senderCountries?.find((c) => c.isoCode === addressCountry)?.id
        ].forEach((c) => {
          cityOptions.push({
            value: c.id.toString(),
            display: c.name,
          });
        });

        setSendingCityOptions(cityOptions);
      }
    }
  }, [sendingCities, addressCountry, senderCountries, dispatch]);

  useEffect(() => {
    if (
      (isEmpty(prevUserKYCData) && !isEmpty(userKYCData)) ||
      prevUserKYCData?.identityNumber !== userKYCData?.identityNumber
    ) {
      setFirstName(userKYCData.firstname || '');
      setMiddleName(userKYCData.middlename || '');
      setLastName(userKYCData.lastname || '');
      setGender(userKYCData.gender || '');
      setBirthDate(
        userKYCData.birthDate
          ? new moment(userKYCData.birthDate, 'YYYY-MM-DD').toDate()
          : new Date()
      );
      setAddress1(userKYCData.address1 || '');
      setAddress2(userKYCData.address2 || '');
      setAddressCity(userKYCData.addressCity || '');
      setAddressState(userKYCData.addressState || '');
      setAddressZipCode(userKYCData.addressZipCode || '');
    }
  }, [userKYCData, prevUserKYCData]);

  useEffect(() => {
    if (!!recaptchaToken) {
      dispatch(authValidateRecaptchaToken(recaptchaToken));
    }
  }, [dispatch, recaptchaToken]);

  // Handlers
  const handlePhoneNumberChange = (event) => {
    const regex = /^\d+$/;
    if (
      event.target.value === '' ||
      (!!!event.target.value.startsWith('0') && regex.test(event.target.value))
    ) {
      setPhoneNumber(event.target.value);
    }
  };

  const handleIdentityNumberChange = (identityNumber) => {
    if (/^[a-z0-9-]+$/i.test(identityNumber) || !!!identityNumber) {
      //this.props.clearUserKYCError();
      setIdentityNumber(identityNumber);
      // const identityNumberLength = parseInt(
      //   config.REACT_APP_USER_KYC_IDENTITY_NUMBER_LENGTH
      // );
      clearTimeout(identityNumbertInputTimer);
      if (
        config.REACT_APP_UM_USER_KYC_ENABLED === 'true' &&
        identityNumber.length === ssnLength &&
        config.REACT_APP_USER_KYC_COUNTRIES &&
        config.REACT_APP_USER_KYC_COUNTRIES.split(',').includes(addressCountry)
      ) {
        dispatch(setCancelIncoming(false));
        identityNumbertInputTimer = setTimeout(() => {
          dispatch(getUserKYCData(identityNumber));
        }, 1500);
      } else {
        dispatch(setCancelIncoming(true));
        clearTimeout(identityNumbertInputTimer);
        dispatch(clearKYCUserData());
      }
    }
  };

  const handleBirthDateChange = (newValue) => {
    const dateToday = new Date();

    if (!(newValue instanceof Date && !isNaN(newValue))) {
      setBirthDateError('Invalid birth date. dd/MM/yyyy');
      return;
    } else if (
      newValue >
      new Date(
        dateToday.getFullYear() - 18,
        dateToday.getMonth(),
        dateToday.getDate()
      )
    ) {
      setBirthDateError('You should be 18 years of age or older.');
      return;
    }

    setBirthDateError('');
    setBirthDate(newValue);
  };

  const validateLatin = (value) => {
    if (config.REACT_APP_ONLY_LATIN_CHARACTERS === 'true' && value) {
      return onlyLatinCharacters(value);
    }

    return true;
  };

  const checkUserComplete = () => {
    if (
      isBankIdEnabled &&
      config.REACT_APP_AUTH_PROCESSOR_COUNTRIES &&
      config.REACT_APP_AUTH_PROCESSOR_COUNTRIES.split(',').includes(
        addressCountry?.toUpperCase()
      )
    ) {
      return (
        !!!motherMaidenName &&
        (!!!withRecaptcha ||
          !!!config.REACT_APP_RECAPTCHA_ENABLED ||
          config.REACT_APP_RECAPTCHA_ENABLED === 'false' ||
          (!!recaptchaToken && recaptchaTokenValidated)) &&
        !!nationality &&
        ((config.REACT_APP_USE_SENDING_CITIES !== 'true' &&
          !!config.REACT_APP_AUTH_PROCESSOR_COUNTRIES &&
          config.REACT_APP_AUTH_PROCESSOR_COUNTRIES.split(',').includes(
            addressCountry?.toUpperCase()
          )) ||
          (!!addressCity && validateLatin(addressCity))) &&
        !!addressCountry &&
        !!phoneNumber &&
        emailRegex.test(email) &&
        !!identityNumber
      );
    } else {
      return (
        !!!motherMaidenName &&
        (!!!withRecaptcha ||
          !!!config.REACT_APP_RECAPTCHA_ENABLED ||
          config.REACT_APP_RECAPTCHA_ENABLED === 'false' ||
          (!!recaptchaToken && recaptchaTokenValidated)) &&
        (!!!config.REACT_APP_USER_KYC_COUNTRIES ||
          !!!config.REACT_APP_USER_KYC_COUNTRIES.split(',').includes(
            addressCountry?.toUpperCase()
          ) ||
          (identityNumber && checkPersonnummer(identityNumber))) &&
        !!firstname &&
        validateLatin(firstname) &&
        (!!!middlename || validateLatin(middlename)) &&
        !!lastname &&
        validateLatin(lastname) &&
        !!gender &&
        gender !== 'selectgender' &&
        !!nationality &&
        !!birthDate &&
        !birthDateError &&
        !!address1 &&
        validateLatin(address1) &&
        (!!!address2 || validateLatin(address2)) &&
        !!addressCity &&
        validateLatin(addressCity) &&
        (hideState || (!!addressState && validateLatin(addressState))) &&
        (hideZipCode || (!!addressZipCode && validateLatin(addressZipCode))) &&
        ((!!addressCountry && !!phoneNumber && emailRegex.test(email)) ||
          (model.account &&
            model.account.status !== 1 &&
            model.account.status !== 4)) &&
        (!!!withPassword ||
          (password === repeatPassword &&
            isStrongPassword(password, {
              minLength: parseInt(pwReqConfigValues[0]),
              minLowercase: parseInt(pwReqConfigValues[1]),
              minUppercase: parseInt(pwReqConfigValues[2]),
              minNumbers: parseInt(pwReqConfigValues[3]),
              minSymbols: parseInt(pwReqConfigValues[4]),
              returnScore: false,
            })))
      );
    }
  };

  const handleContinue = () => {
    setTouched(true);

    if (!checkUserComplete()) {
      return;
    }

    let tempPhoneNumber =
      CountryDialIsoFlags.find((c) => c.isoCode === addressCountry)?.dialCode +
      phoneNumber;
    if (config.REACT_APP_PHONE_FORMAT) {
      tempPhoneNumber = config.REACT_APP_PHONE_FORMAT;
      tempPhoneNumber = tempPhoneNumber.replace(
        'COUNTRYCODE',
        CountryDialIsoFlags.find(
          (c) => c.isoCode === addressCountry
        )?.dialCode?.replace(new RegExp('^\\+'), '')
      );
      tempPhoneNumber = tempPhoneNumber.replace('PHONENUMBER', phoneNumber);
    }

    if (
      isBankIdEnabled &&
      config.REACT_APP_AUTH_PROCESSOR_COUNTRIES &&
      config.REACT_APP_AUTH_PROCESSOR_COUNTRIES.split(',').includes(
        addressCountry?.toUpperCase()
      )
    ) {
      dispatch(
        authProcessorLogin(
          identityNumber,
          addressCountry,
          false,
          senderCountries?.find((c) => c.isoCode === addressCountry)?.id,
          addressCity,
          nationality,
          email?.trim()?.toLowerCase(),
          tempPhoneNumber,
          config.REACT_APP_AUTH_PROCESSOR_REGISTRATION_RETURNLINK,
          config.REACT_APP_AUTH_PROCESSOR_REGISTRATION_CANCELLINK,
          config.REACT_APP_AUTH_PROCESSOR_REGISTRATION_REJECTLINK
        )
      );
    } else {
      let registerUserPayload = {
        firstname,
        lastname,
        gender,
        birthDate,
        nationality,
        addressId: model.addressId,
        address1,
        address2,
        addressCity,
        addressCountry: senderCountries?.find(
          (c) => c.isoCode === addressCountry
        )?.id,
        email: email?.trim()?.toLowerCase(),
        phoneNumber: tempPhoneNumber,
      };
      if (!hideMiddleName) {
        registerUserPayload.middlename = middlename;
      }
      if (!hideState) {
        registerUserPayload.addressState = addressState;
      }
      if (!hideZipCode) {
        registerUserPayload.addressZipCode = addressZipCode;
      }
      if (!isEmpty(userKYCData)) {
      }
      if (withPassword) {
        registerUserPayload.password = password;
      }

      if (
        identityNumber &&
        config.REACT_APP_AUTH_PROCESSOR_COUNTRIES &&
        config.REACT_APP_AUTH_PROCESSOR_COUNTRIES.split(',').includes(
          addressCountry?.toUpperCase()
        )
      ) {
        registerUserPayload.documentNumber = identityNumber;
      }

      onSubmit(registerUserPayload);
    }
  };

  const handleRecaptchaChange = (token) => {
    setRecaptchaToken(token);
    if (!!!token) {
      dispatch(authClearRecaptchaTokenValidated());
    }
  };

  const checkPersonnummer = (str) => {
    return /^\d+$/.test(str) && str && str.length === ssnLength;
  };

  // render
  return (
    <StyledEngineProvider injectFirst>
      <Collapse in={isAuthProcessorError}>
        <Box marginBottom="5px">
          <Alert
            severity="error"
            onClose={() => {
              clearAuthError();
            }}
          >
            {authProcessorErrorMessage}
          </Alert>
        </Box>
      </Collapse>
      <Box mb={3}>
        {config.REACT_APP_REGISTRATION_COUNTRY_DEFAULT_ONLY !== 'true' && (
          <StyledSelect
            id="User-country"
            label="Sending Country"
            options={sendingCountryOptions}
            formControlVariant="outlined"
            fullWidth
            required
            listItemIcon
            value={addressCountry}
            handleChange={(e) => {
              setAddressCountry(e.target.value);
              setIdentityNumber('');
              setIdPlaceHolder(
                configCountrySSNFormat.find(
                  (cf) => cf.countryCode === e.target.value
                )?.placeHolder || 'Identity Number'
              );
              setSSNLength(
                configCountrySSNFormat.find(
                  (cf) => cf.countryCode === e.target.value
                )?.length || 100
              );
            }}
            disabled={
              (!!model.id &&
                config.REACT_APP_PROFILE_UPDATE_ENABLED !== 'true') ||
              (model.account &&
                model.account.status !== 1 &&
                model.account.status !== 4 &&
                true)
            }
            helperText={
              model.account &&
              model.account.status !== 1 &&
              model.account.status !== 4 &&
              true
                ? "Country is locked and can't be updated"
                : undefined
            }
            touched={touched}
          />
        )}
        {config.REACT_APP_USER_KYC_COUNTRIES &&
          config.REACT_APP_USER_KYC_COUNTRIES.split(',').includes(
            addressCountry?.toUpperCase()
          ) &&
          !(model.id && true) && (
            <>
              <StyledTextField
                id="User-identityNumber"
                label={
                  config.REACT_APP_USER_IDENTITY_NUMBER_LABEL ||
                  'Identity Number'
                }
                placeholder={idPlaceHolder}
                note={
                  config.REACT_APP_USER_KYC_COUNTRIES &&
                  config.REACT_APP_USER_KYC_COUNTRIES.split(',').includes(
                    addressCountry
                  ) &&
                  !isBankIdEnabled &&
                  '(Entering your identity number will auto-fill your details.)'
                }
                textFieldVariant="outlined"
                fullWidth
                required
                value={identityNumber}
                handleChange={(e) => {
                  handleIdentityNumberChange(e.target.value);
                }}
                error={
                  errorKYC ||
                  (identityNumber && !checkPersonnummer(identityNumber))
                }
                errorMessage={
                  errorMessageKYC ||
                  (identityNumber && !checkPersonnummer(identityNumber)
                    ? 'Invalid number. ' + idPlaceHolder
                    : undefined)
                }
                touched={touched}
              />
            </>
          )}
        <StyledTextField
          id="User-phoneNumber"
          label="Phone Number"
          placeholder="Phone Number"
          type="text"
          textFieldVariant="outlined"
          fullWidth
          required
          value={phoneNumber}
          startAdornment={
            <>
              {addressCountry.toUpperCase() === 'XX' ? (
                <img
                  style={{
                    display: 'inline-block',
                    width: '1em',
                    height: '1em',
                    verticalAlign: 'middle',
                  }}
                  alt={addressCountry.toUpperCase()}
                  src={DefactoCountryFlag('xx')}
                />
              ) : (
                <ReactCountryFlag
                  svg
                  countryCode={addressCountry.toUpperCase()}
                />
              )}
              <Typography ml={1}>
                {
                  CountryDialIsoFlags.find((c) => c.isoCode === addressCountry)
                    ?.dialCode
                }
              </Typography>
            </>
          }
          handleChange={handlePhoneNumberChange}
          disabled={
            (!!model.id &&
              config.REACT_APP_PROFILE_UPDATE_ENABLED !== 'true') ||
            (model.account &&
              model.account.status !== 1 &&
              model.account.status !== 4 &&
              true)
          }
          helperText={
            model.account &&
            model.account.status !== 1 &&
            model.account.status !== 4 &&
            true
              ? "Phone number is locked and can't be updated"
              : undefined
          }
          touched={touched}
        />
        <StyledTextField
          id="User-email"
          label="Email"
          placeholder="Email Address"
          type="email"
          textFieldVariant="outlined"
          fullWidth
          required
          value={email}
          handleChange={(e) => {
            setEmail(e.target.value);
          }}
          disabled={
            (!!model.id &&
              config.REACT_APP_PROFILE_UPDATE_ENABLED !== 'true') ||
            (model.account &&
              model.account.status !== 1 &&
              model.account.status !== 4 &&
              true)
          }
          helperText={
            model.account &&
            model.account.status !== 1 &&
            model.account.status !== 4 &&
            true
              ? "Email is locked and can't be updated"
              : undefined
          }
          touched={touched}
        />
        <input
          type="hidden"
          name="motherMaidenName"
          value=""
          onChange={(e) => {
            setMotherMaidenName(e.target.value);
          }}
        />
        {(!isBankIdEnabled ||
          !config.REACT_APP_AUTH_PROCESSOR_COUNTRIES ||
          !config.REACT_APP_AUTH_PROCESSOR_COUNTRIES.split(',').includes(
            addressCountry?.toUpperCase()
          )) && (
          <>
            <StyledTextField
              id="User-firstname"
              label="First Name"
              placeholder="First Name"
              type="text"
              textFieldVariant="outlined"
              fullWidth
              required
              value={firstname}
              handleChange={(e) => {
                setFirstName(e.target.value);
              }}
              disabled={
                (!!model.id &&
                  config.REACT_APP_PROFILE_UPDATE_ENABLED !== 'true') ||
                (model.account &&
                  model.account.status !== 1 &&
                  model.account.status !== 4 &&
                  true)
              }
              touched={touched}
              isValidateLatin={
                config.REACT_APP_ONLY_LATIN_CHARACTERS === 'true'
              }
            />
            {!hideMiddleName && (
              <StyledTextField
                id="User-middlename"
                label="Middle Name"
                placeholder="Middle Name"
                type="text"
                textFieldVariant="outlined"
                fullWidth
                value={middlename}
                endAdornment={<Typography>Optional</Typography>}
                handleChange={(e) => {
                  setMiddleName(e.target.value);
                }}
                disabled={
                  (!!model.id &&
                    config.REACT_APP_PROFILE_UPDATE_ENABLED !== 'true') ||
                  (model.account &&
                    model.account.status !== 1 &&
                    model.account.status !== 4 &&
                    true)
                }
                touched={touched}
                isValidateLatin={
                  config.REACT_APP_ONLY_LATIN_CHARACTERS === 'true'
                }
              />
            )}
            <StyledTextField
              id="User-lastname"
              label="Last Name"
              placeholder="Last Name"
              type="text"
              textFieldVariant="outlined"
              fullWidth
              required
              value={lastname}
              handleChange={(e) => {
                setLastName(e.target.value);
              }}
              disabled={
                (!!model.id &&
                  config.REACT_APP_PROFILE_UPDATE_ENABLED !== 'true') ||
                (model.account &&
                  model.account.status !== 1 &&
                  model.account.status !== 4 &&
                  true)
              }
              touched={touched}
              isValidateLatin={
                config.REACT_APP_ONLY_LATIN_CHARACTERS === 'true'
              }
            />
            <StyledSelect
              id="User-gender"
              label="Gender"
              options={[
                {
                  value: 'selectgender',
                  display: 'Select Gender',
                },
                {
                  value: 'female',
                  display: 'Female',
                },
                {
                  value: 'male',
                  display: 'Male',
                },
                {
                  value: 'nonbinary',
                  display: 'Non-binary',
                },
              ]}
              formControlVariant="outlined"
              fullWidth
              required
              noSelectedValue="selectgender"
              value={gender}
              handleChange={(e) => {
                setGender(e.target.value);
              }}
              disabled={
                (!!model.id &&
                  config.REACT_APP_PROFILE_UPDATE_ENABLED !== 'true') ||
                (model.account &&
                  model.account.status !== 1 &&
                  model.account.status !== 4 &&
                  true)
              }
              touched={touched}
            />
          </>
        )}
        <StyledSelect
          id="User-nationality"
          label="Nationality"
          options={NationalityCountryCodes.filter(
            (c) =>
              (config.REACT_APP_SOVEREIGN_COUNTRIES_ONLY === 'true' &&
                SovereignCountriesList.some(
                  (sc) =>
                    sc.alpha3.toUpperCase() === c.alpha_3_code.toUpperCase()
                )) ||
              config.REACT_APP_SOVEREIGN_COUNTRIES_ONLY !== 'true'
          )
            .sort((a, b) =>
              a.nationality > b.nationality
                ? 1
                : b.nationality > a.nationality
                ? -1
                : 0
            )
            .map((c, index) => {
              return {
                display: c.nationality,
                value: c.alpha_2_code,
                listItemIcon:
                  c.alpha_2_code.toUpperCase() === 'XX' ? (
                    <img
                      style={{
                        display: 'inline-block',
                        width: '1em',
                        height: '1em',
                        verticalAlign: 'middle',
                      }}
                      alt={c.alpha_2_code.toUpperCase()}
                      src={DefactoCountryFlag('xx')}
                    />
                  ) : (
                    <ReactCountryFlag
                      svg
                      countryCode={c.alpha_2_code.toUpperCase()}
                    />
                  ),
              };
            })}
          formControlVariant="outlined"
          fullWidth
          required
          listItemIcon
          value={nationality}
          handleChange={(e) => {
            setNationality(e.target.value);
          }}
          disabled={
            (!!model.id &&
              config.REACT_APP_PROFILE_UPDATE_ENABLED !== 'true') ||
            (model.account &&
              model.account.status !== 1 &&
              model.account.status !== 4 &&
              true)
          }
          touched={touched}
        />
        {(!isBankIdEnabled ||
          !config.REACT_APP_AUTH_PROCESSOR_COUNTRIES ||
          !config.REACT_APP_AUTH_PROCESSOR_COUNTRIES.split(',').includes(
            addressCountry?.toUpperCase()
          )) && (
          <>
            <Box>
              {config.REACT_APP_DATE_SELECTION_MODE === 'dropdown' ? (
                <StyledDateSelect
                  id="User-birthDate"
                  label="Birth Date"
                  formControlVariant="outlined"
                  fullWidth
                  required
                  value={birthDate}
                  minDate={
                    new Date(
                      new Date().setFullYear(new Date().getFullYear() - 100)
                    )
                  }
                  maxDate={
                    new Date(
                      new Date().setFullYear(new Date().getFullYear() - 18)
                    )
                  }
                  handleChange={handleBirthDateChange}
                  disabled={
                    (!!model.id &&
                      config.REACT_APP_PROFILE_UPDATE_ENABLED !== 'true') ||
                    (model.account &&
                      model.account.status !== 1 &&
                      model.account.status !== 4 &&
                      true)
                  }
                  touched={touched}
                />
              ) : (
                <>
                  <InputLabel
                    required
                    sx={{
                      '& .MuiInputLabel-asterisk': {
                        color: 'red',
                      },
                    }}
                  >
                    Birth Date
                  </InputLabel>
                  <FormControl
                    fullWidth
                    sx={{
                      backgroundColor: '#FFF',
                      marginBottom: '10px',
                      '& .MuiTextField-root': {
                        '& .MuiInputBase-root': {
                          '& .MuiInputBase-input': {
                            padding: '10px 0px 10px 10px',
                          },
                        },
                      },
                    }}
                  >
                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                      <DatePicker
                        inputFormat="dd/MM/yyyy"
                        value={birthDate}
                        minDate={new Date().setFullYear(
                          new Date().getFullYear() - 100
                        )}
                        maxDate={new Date().setFullYear(
                          new Date().getFullYear() - 18
                        )}
                        onChange={handleBirthDateChange}
                        renderInput={(params) => <TextField {...params} />}
                        disabled={
                          (!!model.id &&
                            config.REACT_APP_PROFILE_UPDATE_ENABLED !==
                              'true') ||
                          (model.account &&
                            model.account.status !== 1 &&
                            model.account.status !== 4 &&
                            true)
                        }
                      />
                    </LocalizationProvider>
                  </FormControl>
                </>
              )}
              <Collapse in={birthDateError !== ''}>
                <InputLabel>
                  <Typography fontSize="0.7rem" color="red" mb={1}>
                    {birthDateError}
                  </Typography>
                </InputLabel>
              </Collapse>
            </Box>
            <StyledTextField
              id="User-address"
              label="Address"
              placeholder="Address"
              type="text"
              textFieldVariant="outlined"
              fullWidth
              required
              value={address1}
              handleChange={(e) => {
                setAddress1(e.target.value);
              }}
              touched={touched}
              isValidateLatin={
                config.REACT_APP_ONLY_LATIN_CHARACTERS === 'true'
              }
              disabled={
                !!model.id && config.REACT_APP_PROFILE_UPDATE_ENABLED !== 'true'
              }
            />
            <StyledTextField
              id="User-address2"
              label="Address 2"
              placeholder="Address 2"
              type="text"
              textFieldVariant="outlined"
              fullWidth
              value={address2}
              endAdornment={<Typography>Optional</Typography>}
              handleChange={(e) => {
                setAddress2(e.target.value);
              }}
              touched={touched}
              isValidateLatin={
                config.REACT_APP_ONLY_LATIN_CHARACTERS === 'true'
              }
              disabled={
                !!model.id && config.REACT_APP_PROFILE_UPDATE_ENABLED !== 'true'
              }
            />
          </>
        )}
        {config.REACT_APP_USE_SENDING_CITIES === 'true' && (
          <StyledSelect
            id="User-city"
            label="City"
            options={sendingCityOptions}
            formControlVariant="outlined"
            fullWidth
            required
            listItemIcon
            value={addressCity}
            handleChange={(e) => {
              setAddressCity(e.target.value);
            }}
            touched={touched}
            disabled={
              !!model.id && config.REACT_APP_PROFILE_UPDATE_ENABLED !== 'true'
            }
          />
        )}
        {((config.REACT_APP_USE_SENDING_CITIES !== 'true' &&
          (!config.REACT_APP_AUTH_PROCESSOR_COUNTRIES ||
            !config.REACT_APP_AUTH_PROCESSOR_COUNTRIES.split(',').includes(
              addressCountry?.toUpperCase()
            ))) ||
          !isBankIdEnabled) && (
          <StyledTextField
            id="User-city"
            label="City"
            placeholder="City"
            type="text"
            textFieldVariant="outlined"
            fullWidth
            required
            value={addressCity}
            handleChange={(e) => {
              setAddressCity(e.target.value);
            }}
            touched={touched}
            isValidateLatin={config.REACT_APP_ONLY_LATIN_CHARACTERS === 'true'}
            disabled={
              !!model.id && config.REACT_APP_PROFILE_UPDATE_ENABLED !== 'true'
            }
          />
        )}
        {(!isBankIdEnabled ||
          !config.REACT_APP_AUTH_PROCESSOR_COUNTRIES ||
          !config.REACT_APP_AUTH_PROCESSOR_COUNTRIES.split(',').includes(
            addressCountry?.toUpperCase()
          )) && (
          <>
            {!hideState && (
              <StyledTextField
                id="User-state"
                label="State"
                placeholder="State"
                type="text"
                textFieldVariant="outlined"
                fullWidth
                required
                value={addressState}
                handleChange={(e) => {
                  setAddressState(e.target.value);
                }}
                touched={touched}
                isValidateLatin={
                  config.REACT_APP_ONLY_LATIN_CHARACTERS === 'true'
                }
                disabled={
                  !!model.id &&
                  config.REACT_APP_PROFILE_UPDATE_ENABLED !== 'true'
                }
              />
            )}
            {!hideZipCode && (
              <StyledTextField
                id="User-zipCode"
                label="Zip Code"
                placeholder="Zip Code"
                type="text"
                textFieldVariant="outlined"
                fullWidth
                required
                value={addressZipCode}
                handleChange={(e) => {
                  setAddressZipCode(e.target.value);
                }}
                touched={touched}
                isValidateLatin={
                  config.REACT_APP_ONLY_LATIN_CHARACTERS === 'true'
                }
                disabled={
                  !!model.id &&
                  config.REACT_APP_PROFILE_UPDATE_ENABLED !== 'true'
                }
              />
            )}
            {withPassword && (
              <>
                <Box marginBottom="5px">
                  <Alert severity="info">
                    {`Minimum: ${pwReqConfigValues[0]} characters${
                      pwReqConfigValues[1] && parseInt(pwReqConfigValues[1]) > 0
                        ? `, ${pwReqConfigValues[1]} Uppercase`
                        : ''
                    }${
                      pwReqConfigValues[2] && parseInt(pwReqConfigValues[2]) > 0
                        ? `, ${pwReqConfigValues[2]} Lowercase`
                        : ''
                    }${
                      pwReqConfigValues[3] && parseInt(pwReqConfigValues[3]) > 0
                        ? `, ${pwReqConfigValues[3]} Numerical`
                        : ''
                    }${
                      pwReqConfigValues[4] && parseInt(pwReqConfigValues[4]) > 0
                        ? `, ${pwReqConfigValues[4]} Special`
                        : ''
                    }`}
                  </Alert>
                </Box>
                <StyledTextField
                  id="User-password"
                  label="Please enter your password"
                  type={showPassword ? 'passwordtext' : 'password'}
                  textFieldVariant="outlined"
                  fullWidth
                  required
                  value={password}
                  handleChange={(e) => {
                    setPassword(e.target.value);
                  }}
                  inputProps={{ autoComplete: 'new-password' }}
                  endAdornment={
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() => setShowPassword(!showPassword)}
                      onMouseDown={(event) => event.preventDefault()}
                      edge="end"
                    >
                      {showPassword ? <Visibility /> : <VisibilityOff />}
                    </IconButton>
                  }
                  touched={touched}
                />
                <StyledTextField
                  id="User-repeat-password"
                  label="Please re-enter your password"
                  type={showRepeatPassword ? 'passwordtext' : 'password'}
                  textFieldVariant="outlined"
                  fullWidth
                  required
                  value={repeatPassword}
                  handleChange={(e) => {
                    setRepeatPassword(e.target.value);
                  }}
                  inputProps={{ autoComplete: 'new-password' }}
                  endAdornment={
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() => setShowRepeatPassword(!showRepeatPassword)}
                      onMouseDown={(event) => event.preventDefault()}
                      edge="end"
                    >
                      {showRepeatPassword ? <Visibility /> : <VisibilityOff />}
                    </IconButton>
                  }
                  touched={touched}
                />
              </>
            )}
          </>
        )}
        {!!withRecaptcha && (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            flexDirection="column"
          >
            <ReCAPTCHA
              sitekey={config.REACT_APP_RECAPTCHA_SITEKEY}
              onChange={handleRecaptchaChange}
            />
            <Collapse in={touched && !!!recaptchaToken}>
              <InputLabel>
                <Typography fontSize="0.7rem" color="red" mb={1}>
                  Please toggle reCAPTCHA.
                </Typography>
              </InputLabel>
            </Collapse>
          </Box>
        )}
        <Backdrop
          className={backdropStyles}
          style={{ position: 'absolute' }}
          open={isKYCLoading || isAuthProcessorLoading}
        >
          <CircularProgress color="inherit" />
        </Backdrop>
      </Box>
      {(!!!model.id ||
        (!!model.id && config.REACT_APP_PROFILE_UPDATE_ENABLED === 'true')) && (
        <Button
          fullWidth
          size="large"
          variant="contained"
          color="primary"
          type="button"
          disabled={
            withRecaptcha &&
            config.REACT_APP_RECAPTCHA_ENABLED === 'true' &&
            (!!!recaptchaToken || !!!recaptchaTokenValidated)
          }
          onClick={handleContinue}
        >
          {submitButtonText}
        </Button>
      )}
    </StyledEngineProvider>
  );
};

export default User;
